import React, { useEffect, useMemo, useState } from 'react';
import { get } from '../api';

const LANDING_PAGES = [
  { id: 'case-study', label: 'Case Study', url: 'https://freedomincomeoptions.com/case-study/', campaign: 'case_study' },
  { id: 'roadmap', label: 'Roadmap', url: 'https://freedomincomeoptions.com/roadmap/', campaign: 'roadmap' },
];

const SOURCE_PRESETS = [
  { id: 'telegram', label: 'Telegram', source: 'telegram', medium: 'social', content: 'main_post' },
  { id: 'linkedin', label: 'LinkedIn Post', source: 'linkedin', medium: 'social', content: 'main_post' },
  { id: 'youtube', label: 'YouTube', source: 'youtube', medium: 'video', content: 'description' },
  { id: 'substack', label: 'Substack Article', source: 'substack', medium: 'newsletter', content: 'article_link' },
  { id: 'podcast', label: 'Podcast Show Notes', source: 'podcast', medium: 'audio', content: 'show_notes' },
  { id: 'email-broadcast', label: 'Email Broadcast', source: 'email', medium: 'broadcast', content: 'body_link' },
  { id: 'email-sequence', label: 'Email Sequence', source: 'email', medium: 'sequence', content: 'body_link' },
];

function fmtNum(value) {
  return Number(value || 0).toLocaleString();
}

function fmtMoney(value) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(value || 0));
}

function fmtPct(value) {
  if (value === null || value === undefined) return '—';
  return `${Number(value).toFixed(2)}%`;
}

function fmtLabel(value) {
  return String(value || 'unknown')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, char => char.toUpperCase());
}

function fmtDate(value) {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function sourceKey(row, prefix = '') {
  return [
    row[`${prefix}source`] || row.source || 'direct',
    row[`${prefix}medium`] || row.medium || 'unknown',
    row[`${prefix}campaign`] || row.campaign || 'none',
    row[`${prefix}content`] || row.content || 'none',
  ].join('\u001f');
}

function hasActivity(row) {
  return Number(row.page_views || 0) > 0
    || Number(row.cta_clicks || 0) > 0
    || Number(row.optins || 0) > 0
    || Number(row.buyers || 0) > 0
    || Number(row.revenue || 0) > 0;
}

function buildYoutubeVideoRows(sources) {
  const groups = sources
    .filter(row => String(row.source || '').toLowerCase() === 'youtube')
    .filter(row => String(row.medium || '').toLowerCase() === 'video')
    .filter(row => row.content && row.content !== 'none')
    .reduce((map, row) => {
      const key = row.content || 'unknown';
      const current = map.get(key) || {
        key,
        video: key,
        page_views: 0,
        cta_clicks: 0,
        optins: 0,
        buyers: 0,
        revenue: 0,
        funnels: new Set(),
        campaigns: new Set(),
        rows: [],
      };

      current.page_views += Number(row.page_views || 0);
      current.cta_clicks += Number(row.cta_clicks || 0);
      current.optins += Number(row.optins || 0);
      current.buyers += Number(row.buyers || 0);
      current.revenue += Number(row.revenue || 0);
      current.funnels.add(row.label || row.funnel_id || 'Unknown funnel');
      current.campaigns.add(row.campaign || 'none');
      current.rows.push(row);
      map.set(key, current);
      return map;
    }, new Map());

  return Array.from(groups.values())
    .map(row => ({
      ...row,
      funnels: Array.from(row.funnels),
      campaigns: Array.from(row.campaigns),
      optin_rate: row.page_views > 0 ? (row.optins / row.page_views) * 100 : null,
      buyer_rate: row.optins > 0 ? (row.buyers / row.optins) * 100 : null,
      revenue_per_view: row.page_views > 0 ? row.revenue / row.page_views : 0,
      revenue_per_click: row.cta_clicks > 0 ? row.revenue / row.cta_clicks : 0,
      revenue_per_optin: row.optins > 0 ? row.revenue / row.optins : 0,
    }))
    .filter(hasActivity)
    .sort((a, b) => (
      (b.revenue - a.revenue)
      || (b.buyers - a.buyers)
      || (b.revenue_per_optin - a.revenue_per_optin)
      || (b.optins - a.optins)
      || a.video.localeCompare(b.video)
    ));
}

function Card({ label, value, sub, tone = 'default' }) {
  const color = tone === 'green' ? 'var(--green)' : tone === 'blue' ? 'var(--blue)' : '#fff';
  return (
    <div style={{ background: '#141414', border: '0.5px solid #2a2a2a', borderRadius: 12, padding: '18px 20px' }}>
      <div style={{ fontSize: 10, fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#6B7280', marginBottom: 8 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 700, color }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: '#71717A', marginTop: 5 }}>{sub}</div>}
    </div>
  );
}

function Input({ label, value, onChange, placeholder }) {
  return (
    <label style={{ display: 'grid', gap: 6 }}>
      <span style={{ fontSize: 10, fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#6B7280' }}>{label}</span>
      <input
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        style={{
          width: '100%',
          padding: '11px 12px',
          borderRadius: 8,
          border: '0.5px solid #2a2a2a',
          background: '#0f0f0f',
          color: '#fff',
          fontSize: 16,
          fontFamily: "'DM Sans', sans-serif",
          outline: 'none',
        }}
      />
    </label>
  );
}

function TrackingLinkBuilder() {
  const [pageId, setPageId] = useState('case-study');
  const [presetId, setPresetId] = useState('telegram');
  const [source, setSource] = useState('telegram');
  const [medium, setMedium] = useState('social');
  const [campaign, setCampaign] = useState('case_study');
  const [content, setContent] = useState('main_post');
  const [copied, setCopied] = useState(false);

  const selectedPage = LANDING_PAGES.find(page => page.id === pageId) || LANDING_PAGES[0];

  const updatePage = (nextPageId) => {
    const nextPage = LANDING_PAGES.find(page => page.id === nextPageId) || LANDING_PAGES[0];
    setPageId(nextPage.id);
    setCampaign(nextPage.campaign);
  };

  const updatePreset = (nextPresetId) => {
    const preset = SOURCE_PRESETS.find(item => item.id === nextPresetId);
    setPresetId(nextPresetId);
    if (!preset) return;
    setSource(preset.source);
    setMedium(preset.medium);
    setContent(preset.content);
  };

  const url = useMemo(() => {
    const params = new URLSearchParams();
    params.set('utm_source', source.trim() || 'unknown');
    params.set('utm_medium', medium.trim() || 'unknown');
    params.set('utm_campaign', campaign.trim() || 'case_study');
    params.set('utm_content', content.trim() || 'main');
    return `${selectedPage.url}?${params.toString()}`;
  }, [selectedPage.url, source, medium, campaign, content]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div style={{ background: '#141414', border: '0.5px solid #2a2a2a', borderRadius: 12, padding: 20 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Tracking Link Builder</h3>
          <p style={{ margin: '5px 0 0', fontSize: 13, color: '#71717A', maxWidth: 720 }}>
            Use this for every public share, including emails. The page still tracks raw visits, but UTMs tell us which channel produced opt-ins, buyers, and revenue.
          </p>
        </div>
        <span style={{ padding: '4px 10px', borderRadius: 999, background: 'rgba(16,185,129,0.12)', color: 'var(--green)', fontSize: 10, fontWeight: 800, letterSpacing: '0.06em' }}>DEFAULT RULE</span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 14 }}>
        <label style={{ display: 'grid', gap: 6 }}>
          <span style={{ fontSize: 10, fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#6B7280' }}>Landing Page</span>
          <select value={pageId} onChange={e => updatePage(e.target.value)} style={{ width: '100%', padding: '11px 12px', borderRadius: 8, border: '0.5px solid #2a2a2a', background: '#0f0f0f', color: '#fff', fontSize: 16, fontFamily: "'DM Sans', sans-serif", outline: 'none' }}>
            {LANDING_PAGES.map(page => <option key={page.id} value={page.id}>{page.label}</option>)}
          </select>
        </label>
        <label style={{ display: 'grid', gap: 6 }}>
          <span style={{ fontSize: 10, fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#6B7280' }}>Channel Preset</span>
          <select value={presetId} onChange={e => updatePreset(e.target.value)} style={{ width: '100%', padding: '11px 12px', borderRadius: 8, border: '0.5px solid #2a2a2a', background: '#0f0f0f', color: '#fff', fontSize: 16, fontFamily: "'DM Sans', sans-serif", outline: 'none' }}>
            {SOURCE_PRESETS.map(preset => <option key={preset.id} value={preset.id}>{preset.label}</option>)}
            <option value="custom">Custom</option>
          </select>
        </label>
        <Input label="Source" value={source} onChange={(value) => { setSource(value); setPresetId('custom'); }} placeholder="telegram" />
        <Input label="Medium" value={medium} onChange={(value) => { setMedium(value); setPresetId('custom'); }} placeholder="social" />
        <Input label="Campaign" value={campaign} onChange={setCampaign} placeholder="case_study" />
        <Input label="Content / Variant" value={content} onChange={(value) => { setContent(value); setPresetId('custom'); }} placeholder="main_post" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 10, alignItems: 'stretch' }}>
        <div style={{ background: '#0f0f0f', border: '0.5px solid #2a2a2a', borderRadius: 8, padding: '12px 14px', color: '#d4d4d4', fontSize: 12, lineHeight: 1.6, wordBreak: 'break-all', fontFamily: "'JetBrains Mono', monospace" }}>{url}</div>
        <button onClick={copy} style={{ padding: '0 18px', borderRadius: 8, border: 'none', background: copied ? 'var(--green)' : 'var(--blue)', color: '#fff', fontWeight: 800, cursor: 'pointer' }}>
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
    </div>
  );
}

function SourceSummaryCards({ sources }) {
  const prioritySources = ['youtube', 'telegram', 'linkedin', 'substack', 'podcast', 'email'];
  const totals = sources.reduce((map, row) => {
    const key = row.source || 'direct';
    const current = map[key] || { source: key, page_views: 0, cta_clicks: 0, optins: 0, buyers: 0, revenue: 0 };
    current.page_views += Number(row.page_views || 0);
    current.cta_clicks += Number(row.cta_clicks || 0);
    current.optins += Number(row.optins || 0);
    current.buyers += Number(row.buyers || 0);
    current.revenue += Number(row.revenue || 0);
    map[key] = current;
    return map;
  }, {});

  const rows = prioritySources.map(source => {
    const row = totals[source] || { source, page_views: 0, cta_clicks: 0, optins: 0, buyers: 0, revenue: 0 };
    return {
      ...row,
      revenue_per_click: row.cta_clicks > 0 ? row.revenue / row.cta_clicks : 0,
      revenue_per_optin: row.optins > 0 ? row.revenue / row.optins : 0,
    };
  });

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
      {rows.map(row => (
        <div key={row.source} style={{ background: '#141414', border: '0.5px solid #2a2a2a', borderRadius: 12, padding: 18 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div style={{ fontSize: 16, fontWeight: 800, textTransform: 'capitalize' }}>{row.source}</div>
            <span style={{ padding: '3px 8px', borderRadius: 999, background: 'rgba(59,130,246,0.12)', color: 'var(--blue)', fontSize: 10, fontWeight: 800 }}>SOURCE</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
            <div><div style={{ color: '#6B7280', fontSize: 10, fontFamily: "'JetBrains Mono', monospace" }}>OPT-INS</div><div style={{ fontSize: 20, fontWeight: 800, color: 'var(--blue)' }}>{fmtNum(row.optins)}</div></div>
            <div><div style={{ color: '#6B7280', fontSize: 10, fontFamily: "'JetBrains Mono', monospace" }}>REVENUE</div><div style={{ fontSize: 20, fontWeight: 800, color: 'var(--green)' }}>{fmtMoney(row.revenue)}</div></div>
            <div><div style={{ color: '#6B7280', fontSize: 10, fontFamily: "'JetBrains Mono', monospace" }}>REV / CLICK</div><div style={{ fontSize: 14, fontWeight: 700 }}>{fmtMoney(row.revenue_per_click)}</div></div>
            <div><div style={{ color: '#6B7280', fontSize: 10, fontFamily: "'JetBrains Mono', monospace" }}>REV / OPT-IN</div><div style={{ fontSize: 14, fontWeight: 700 }}>{fmtMoney(row.revenue_per_optin)}</div></div>
          </div>
        </div>
      ))}
    </div>
  );
}

function YoutubeVideoRevenue({ sources }) {
  const [expandedVideo, setExpandedVideo] = useState(null);
  const videos = useMemo(() => buildYoutubeVideoRows(sources), [sources]);
  const topVideo = videos[0];

  if (!videos.length) {
    return (
      <div style={{ background: '#141414', border: '0.5px solid #2a2a2a', borderRadius: 12, padding: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>YouTube Video Revenue</h3>
            <p style={{ margin: '5px 0 0', fontSize: 13, color: '#71717A', maxWidth: 780 }}>
              No YouTube video rows with activity in this period yet. Use YouTube links with <strong style={{ color: '#d4d4d4' }}>utm_source=youtube</strong>, <strong style={{ color: '#d4d4d4' }}>utm_medium=video</strong>, and a unique <strong style={{ color: '#d4d4d4' }}>utm_content</strong> per video.
            </p>
          </div>
          <span style={{ padding: '4px 10px', borderRadius: 999, background: 'rgba(107,114,128,0.14)', color: '#9CA3AF', fontSize: 10, fontWeight: 800 }}>WAITING FOR DATA</span>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: '#141414', border: '0.5px solid #2a2a2a', borderRadius: 12, padding: 20, overflowX: 'auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>YouTube Video Revenue</h3>
          <p style={{ margin: '5px 0 0', fontSize: 13, color: '#71717A', maxWidth: 780 }}>
            Ranked by attributed revenue using YouTube UTM content. Click a video to drill into funnel, campaign, and revenue rows.
          </p>
        </div>
        {topVideo && (
          <div style={{ textAlign: 'right', minWidth: 180 }}>
            <div style={{ fontSize: 10, fontFamily: "'JetBrains Mono', monospace", fontWeight: 800, textTransform: 'uppercase', color: '#6B7280', letterSpacing: '0.08em' }}>Top Video</div>
            <div style={{ color: 'var(--green)', fontSize: 20, fontWeight: 800 }}>{fmtMoney(topVideo.revenue)}</div>
            <div style={{ color: '#9CA3AF', fontSize: 12, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{fmtLabel(topVideo.video)}</div>
          </div>
        )}
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ borderBottom: '0.5px solid #2a2a2a' }}>
            {['Video / UTM Content', 'Funnels', 'Views', 'Clicks', 'Leads', 'Buyers', 'Revenue', 'Rev / Lead', 'Buyer %', ''].map(h => (
              <th key={h} style={{ padding: '9px 10px', textAlign: ['Video / UTM Content', 'Funnels', ''].includes(h) ? 'left' : 'right', color: '#6B7280', fontSize: 10, fontFamily: "'JetBrains Mono', monospace", textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {videos.map((video, idx) => {
            const isExpanded = expandedVideo === video.key;
            return (
              <React.Fragment key={video.key}>
                <tr style={{ borderBottom: isExpanded ? 'none' : '0.5px solid #2a2a2a', background: idx === 0 ? 'rgba(16,185,129,0.04)' : 'transparent' }}>
                  <td style={{ padding: '12px 10px', color: '#fff', fontWeight: 700, minWidth: 250 }}>
                    <div>{fmtLabel(video.video)}</div>
                    <div style={{ color: '#71717A', fontSize: 11, marginTop: 3, fontFamily: "'JetBrains Mono', monospace" }}>utm_content={video.video}</div>
                  </td>
                  <td style={{ padding: '12px 10px', color: '#9CA3AF', minWidth: 150 }}>{video.funnels.join(', ')}</td>
                  <td style={{ padding: '12px 10px', textAlign: 'right', color: '#d4d4d4', fontFamily: "'JetBrains Mono', monospace" }}>{fmtNum(video.page_views)}</td>
                  <td style={{ padding: '12px 10px', textAlign: 'right', color: '#d4d4d4', fontFamily: "'JetBrains Mono', monospace" }}>{fmtNum(video.cta_clicks)}</td>
                  <td style={{ padding: '12px 10px', textAlign: 'right', color: '#fff', fontFamily: "'JetBrains Mono', monospace" }}>{fmtNum(video.optins)}</td>
                  <td style={{ padding: '12px 10px', textAlign: 'right', color: '#fff', fontFamily: "'JetBrains Mono', monospace" }}>{fmtNum(video.buyers)}</td>
                  <td style={{ padding: '12px 10px', textAlign: 'right', color: 'var(--green)', fontFamily: "'JetBrains Mono', monospace", fontWeight: 800 }}>{fmtMoney(video.revenue)}</td>
                  <td style={{ padding: '12px 10px', textAlign: 'right', color: '#d4d4d4', fontFamily: "'JetBrains Mono', monospace" }}>{fmtMoney(video.revenue_per_optin)}</td>
                  <td style={{ padding: '12px 10px', textAlign: 'right', color: 'var(--blue)', fontFamily: "'JetBrains Mono', monospace" }}>{fmtPct(video.buyer_rate)}</td>
                  <td style={{ padding: '12px 10px', textAlign: 'left' }}>
                    <button
                      onClick={() => setExpandedVideo(isExpanded ? null : video.key)}
                      style={{ border: '0.5px solid #2a2a2a', background: isExpanded ? 'rgba(59,130,246,0.14)' : '#0f0f0f', color: isExpanded ? 'var(--blue)' : '#d4d4d4', borderRadius: 8, padding: '7px 10px', cursor: 'pointer', fontSize: 12, fontWeight: 800 }}
                    >
                      {isExpanded ? 'Hide' : 'Drill down'}
                    </button>
                  </td>
                </tr>
                {isExpanded && (
                  <tr style={{ borderBottom: '0.5px solid #2a2a2a' }}>
                    <td colSpan={10} style={{ padding: '0 10px 14px' }}>
                      <div style={{ background: '#0f0f0f', border: '0.5px solid #2a2a2a', borderRadius: 10, padding: 12 }}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginBottom: 12 }}>
                          <div><div style={{ color: '#6B7280', fontSize: 10, fontFamily: "'JetBrains Mono', monospace" }}>REVENUE / VIEW</div><div style={{ fontWeight: 800 }}>{fmtMoney(video.revenue_per_view)}</div></div>
                          <div><div style={{ color: '#6B7280', fontSize: 10, fontFamily: "'JetBrains Mono', monospace" }}>REVENUE / CLICK</div><div style={{ fontWeight: 800 }}>{fmtMoney(video.revenue_per_click)}</div></div>
                          <div><div style={{ color: '#6B7280', fontSize: 10, fontFamily: "'JetBrains Mono', monospace" }}>OPT-IN RATE</div><div style={{ fontWeight: 800 }}>{fmtPct(video.optin_rate)}</div></div>
                          <div><div style={{ color: '#6B7280', fontSize: 10, fontFamily: "'JetBrains Mono', monospace" }}>CAMPAIGNS</div><div style={{ fontWeight: 800 }}>{video.campaigns.join(', ')}</div></div>
                        </div>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                          <thead>
                            <tr style={{ borderBottom: '0.5px solid #2a2a2a' }}>
                              {['Funnel', 'Campaign', 'Content', 'Attribution', 'Views', 'Leads', 'Buyers', 'Revenue'].map(h => (
                                <th key={h} style={{ padding: '8px 8px', textAlign: ['Funnel', 'Campaign', 'Content', 'Attribution'].includes(h) ? 'left' : 'right', color: '#6B7280', fontSize: 10, fontFamily: "'JetBrains Mono', monospace", textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {video.rows.map((row, rowIdx) => (
                              <tr key={`${video.key}-${row.funnel_id}-${row.campaign}-${rowIdx}`} style={{ borderBottom: rowIdx === video.rows.length - 1 ? 'none' : '0.5px solid #202020' }}>
                                <td style={{ padding: '8px', color: '#d4d4d4' }}>{row.label || row.funnel_id}</td>
                                <td style={{ padding: '8px', color: '#9CA3AF' }}>{row.campaign}</td>
                                <td style={{ padding: '8px', color: '#9CA3AF' }}>{row.content}</td>
                                <td style={{ padding: '8px', color: row.attribution_type === 'sequence_click' ? 'var(--green)' : '#9CA3AF' }}>{row.sequence_slug || row.attribution_type || 'optin'}</td>
                                <td style={{ padding: '8px', textAlign: 'right', color: '#d4d4d4', fontFamily: "'JetBrains Mono', monospace" }}>{fmtNum(row.page_views)}</td>
                                <td style={{ padding: '8px', textAlign: 'right', color: '#fff', fontFamily: "'JetBrains Mono', monospace" }}>{fmtNum(row.optins)}</td>
                                <td style={{ padding: '8px', textAlign: 'right', color: '#fff', fontFamily: "'JetBrains Mono', monospace" }}>{fmtNum(row.buyers)}</td>
                                <td style={{ padding: '8px', textAlign: 'right', color: 'var(--green)', fontFamily: "'JetBrains Mono', monospace" }}>{fmtMoney(row.revenue)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function BuyerDrilldown({ transactions }) {
  if (!transactions.length) {
    return <div style={{ color: '#71717A', padding: 14, textAlign: 'center' }}>No buyer transactions in this group.</div>;
  }

  return (
    <div style={{ background: '#0f0f0f', border: '0.5px solid #2a2a2a', borderRadius: 10, padding: 12, overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr style={{ borderBottom: '0.5px solid #2a2a2a' }}>
            {['Buyer', 'Product', 'Amount', 'Purchase', 'Original Video/Content', 'Latest Source', 'Final Attribution', 'Days'].map(h => (
              <th key={h} style={{ padding: '8px', textAlign: ['Buyer', 'Product', 'Original Video/Content', 'Latest Source', 'Final Attribution'].includes(h) ? 'left' : 'right', color: '#6B7280', fontSize: 10, fontFamily: "'JetBrains Mono', monospace", textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {transactions.map(tx => (
            <tr key={tx.id} style={{ borderBottom: '0.5px solid #202020' }}>
              <td style={{ padding: '8px', color: '#fff', whiteSpace: 'nowrap' }}>{tx.buyer_email || '—'}</td>
              <td style={{ padding: '8px', color: '#d4d4d4', minWidth: 160 }}>{tx.product_name || tx.membership_id || '—'}</td>
              <td style={{ padding: '8px', textAlign: 'right', color: 'var(--green)', fontFamily: "'JetBrains Mono', monospace", fontWeight: 800 }}>{fmtMoney(tx.amount)}</td>
              <td style={{ padding: '8px', textAlign: 'right', color: '#d4d4d4', whiteSpace: 'nowrap' }}>{fmtDate(tx.processed_at)}</td>
              <td style={{ padding: '8px', color: '#9CA3AF' }} title={tx.original_page_url || ''}>{tx.original_content || 'none'}</td>
              <td style={{ padding: '8px', color: '#9CA3AF' }}>{tx.latest_source || '—'} / {tx.latest_medium || '—'}</td>
              <td style={{ padding: '8px', color: tx.final_attribution_type === 'sequence_click' ? 'var(--green)' : '#9CA3AF' }}>{tx.final_attribution_type || '—'}</td>
              <td style={{ padding: '8px', textAlign: 'right', color: '#d4d4d4', fontFamily: "'JetBrains Mono', monospace" }}>{tx.days_to_purchase === null ? '—' : Number(tx.days_to_purchase).toFixed(1)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RevenueSourceReview({ review }) {
  const [expandedSource, setExpandedSource] = useState(null);
  const [expandedVideo, setExpandedVideo] = useState(null);
  const originalSources = review?.original_sources || [];
  const youtubeOriginal = review?.youtube_original || [];
  const transactions = review?.transactions || [];
  const summary = review?.summary || {};

  const sourceTransactions = (row) => transactions.filter(tx => sourceKey(tx, 'original_') === sourceKey(row));
  const videoTransactions = (row) => transactions.filter(tx => String(tx.original_source || '').toLowerCase() === 'youtube' && (tx.original_content || 'unknown') === row.video);

  return (
    <div style={{ background: '#141414', border: '0.5px solid #2a2a2a', borderRadius: 12, padding: 20, overflowX: 'auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Case Study Buyer Revenue Review</h3>
          <p style={{ margin: '5px 0 0', fontSize: 13, color: '#71717A', maxWidth: 820 }}>
            Paid buyer audit for original <strong style={{ color: '#d4d4d4' }}>case-study</strong> leads only. This is not a lead count — it shows buyers, orders, and revenue by the first known Case Study source, while also showing latest/final attribution for context.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ background: '#0f0f0f', border: '0.5px solid #2a2a2a', borderRadius: 10, padding: '10px 12px', minWidth: 110 }}>
            <div style={{ fontSize: 10, color: '#6B7280', fontFamily: "'JetBrains Mono', monospace", fontWeight: 800 }}>PAID BUYERS</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--blue)' }}>{fmtNum(summary.buyers)}</div>
          </div>
          <div style={{ background: '#0f0f0f', border: '0.5px solid #2a2a2a', borderRadius: 10, padding: '10px 12px', minWidth: 110 }}>
            <div style={{ fontSize: 10, color: '#6B7280', fontFamily: "'JetBrains Mono', monospace", fontWeight: 800 }}>PAID ORDERS</div>
            <div style={{ fontSize: 18, fontWeight: 800 }}>{fmtNum(summary.orders)}</div>
          </div>
          <div style={{ background: '#0f0f0f', border: '0.5px solid #2a2a2a', borderRadius: 10, padding: '10px 12px', minWidth: 130 }}>
            <div style={{ fontSize: 10, color: '#6B7280', fontFamily: "'JetBrains Mono', monospace", fontWeight: 800 }}>REVENUE</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--green)' }}>{fmtMoney(summary.revenue)}</div>
          </div>
        </div>
      </div>

      {youtubeOriginal.length > 0 && (
        <div style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 11, fontFamily: "'JetBrains Mono', monospace", fontWeight: 800, textTransform: 'uppercase', color: '#6B7280', letterSpacing: '0.08em', marginBottom: 8 }}>Original Case Study YouTube Video Revenue</div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '0.5px solid #2a2a2a' }}>
                {['Video / Original UTM Content', 'Buyers', 'Orders', 'Strategy Rev', 'Downstream Rev', 'Total Revenue', ''].map(h => (
                  <th key={h} style={{ padding: '9px 10px', textAlign: ['Video / Original UTM Content', ''].includes(h) ? 'left' : 'right', color: '#6B7280', fontSize: 10, fontFamily: "'JetBrains Mono', monospace", textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {youtubeOriginal.map(row => {
                const isExpanded = expandedVideo === row.video;
                const txs = videoTransactions(row);
                return (
                  <React.Fragment key={row.video}>
                    <tr style={{ borderBottom: isExpanded ? 'none' : '0.5px solid #2a2a2a' }}>
                      <td style={{ padding: '12px 10px', color: '#fff', fontWeight: 700 }}><div>{fmtLabel(row.video)}</div><div style={{ color: '#71717A', fontSize: 11, marginTop: 3, fontFamily: "'JetBrains Mono', monospace" }}>utm_content={row.video}</div></td>
                      <td style={{ padding: '12px 10px', textAlign: 'right', color: '#fff', fontFamily: "'JetBrains Mono', monospace" }}>{fmtNum(row.buyers)}</td>
                      <td style={{ padding: '12px 10px', textAlign: 'right', color: '#d4d4d4', fontFamily: "'JetBrains Mono', monospace" }}>{fmtNum(row.orders)}</td>
                      <td style={{ padding: '12px 10px', textAlign: 'right', color: '#d4d4d4', fontFamily: "'JetBrains Mono', monospace" }}>{fmtMoney(row.strategy_revenue)}</td>
                      <td style={{ padding: '12px 10px', textAlign: 'right', color: '#d4d4d4', fontFamily: "'JetBrains Mono', monospace" }}>{fmtMoney(row.downstream_revenue)}</td>
                      <td style={{ padding: '12px 10px', textAlign: 'right', color: 'var(--green)', fontFamily: "'JetBrains Mono', monospace", fontWeight: 800 }}>{fmtMoney(row.revenue)}</td>
                      <td style={{ padding: '12px 10px' }}><button onClick={() => setExpandedVideo(isExpanded ? null : row.video)} style={{ border: '0.5px solid #2a2a2a', background: isExpanded ? 'rgba(59,130,246,0.14)' : '#0f0f0f', color: isExpanded ? 'var(--blue)' : '#d4d4d4', borderRadius: 8, padding: '7px 10px', cursor: 'pointer', fontSize: 12, fontWeight: 800 }}>{isExpanded ? 'Hide' : `Buyers (${txs.length})`}</button></td>
                    </tr>
                    {isExpanded && <tr style={{ borderBottom: '0.5px solid #2a2a2a' }}><td colSpan={7} style={{ padding: '0 10px 14px' }}><BuyerDrilldown transactions={txs} /></td></tr>}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ fontSize: 11, fontFamily: "'JetBrains Mono', monospace", fontWeight: 800, textTransform: 'uppercase', color: '#6B7280', letterSpacing: '0.08em', marginBottom: 8 }}>Original Case Study Source Revenue</div>
      {originalSources.length === 0 ? (
        <div style={{ color: '#71717A', padding: 24, textAlign: 'center' }}>No paid transactions found for this period.</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: '0.5px solid #2a2a2a' }}>
              {['Original Source', 'Medium', 'Campaign', 'Content', 'Buyers', 'Orders', 'Strategy Rev', 'Downstream Rev', 'Total Revenue', ''].map(h => (
                <th key={h} style={{ padding: '9px 10px', textAlign: ['Original Source', 'Medium', 'Campaign', 'Content', ''].includes(h) ? 'left' : 'right', color: '#6B7280', fontSize: 10, fontFamily: "'JetBrains Mono', monospace", textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {originalSources.map((row, idx) => {
              const key = sourceKey(row);
              const isExpanded = expandedSource === key;
              const txs = sourceTransactions(row);
              return (
                <React.Fragment key={`${key}-${idx}`}>
                  <tr style={{ borderBottom: isExpanded ? 'none' : '0.5px solid #2a2a2a' }}>
                    <td style={{ padding: '12px 10px', color: '#fff', fontWeight: 700 }}>{row.source}</td>
                    <td style={{ padding: '12px 10px', color: '#d4d4d4' }}>{row.medium}</td>
                    <td style={{ padding: '12px 10px', color: '#d4d4d4' }}>{row.campaign}</td>
                    <td style={{ padding: '12px 10px', color: '#9CA3AF' }}>{row.content}</td>
                    <td style={{ padding: '12px 10px', textAlign: 'right', color: '#fff', fontFamily: "'JetBrains Mono', monospace" }}>{fmtNum(row.buyers)}</td>
                    <td style={{ padding: '12px 10px', textAlign: 'right', color: '#d4d4d4', fontFamily: "'JetBrains Mono', monospace" }}>{fmtNum(row.orders)}</td>
                    <td style={{ padding: '12px 10px', textAlign: 'right', color: '#d4d4d4', fontFamily: "'JetBrains Mono', monospace" }}>{fmtMoney(row.strategy_revenue)}</td>
                    <td style={{ padding: '12px 10px', textAlign: 'right', color: '#d4d4d4', fontFamily: "'JetBrains Mono', monospace" }}>{fmtMoney(row.downstream_revenue)}</td>
                    <td style={{ padding: '12px 10px', textAlign: 'right', color: 'var(--green)', fontFamily: "'JetBrains Mono', monospace", fontWeight: 800 }}>{fmtMoney(row.revenue)}</td>
                    <td style={{ padding: '12px 10px' }}><button onClick={() => setExpandedSource(isExpanded ? null : key)} style={{ border: '0.5px solid #2a2a2a', background: isExpanded ? 'rgba(59,130,246,0.14)' : '#0f0f0f', color: isExpanded ? 'var(--blue)' : '#d4d4d4', borderRadius: 8, padding: '7px 10px', cursor: 'pointer', fontSize: 12, fontWeight: 800 }}>{isExpanded ? 'Hide' : `Buyers (${txs.length})`}</button></td>
                  </tr>
                  {isExpanded && <tr style={{ borderBottom: '0.5px solid #2a2a2a' }}><td colSpan={10} style={{ padding: '0 10px 14px' }}><BuyerDrilldown transactions={txs} /></td></tr>}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

function TeachingPanel() {
  const rules = [
    ['utm_source', 'Where traffic came from', 'telegram, linkedin, substack, podcast, x, youtube, email, partner_name'],
    ['utm_medium', 'Channel type', 'social, newsletter, audio, video, broadcast, sequence, referral, paid'],
    ['utm_campaign', 'The funnel or push', 'case_study, strategy_launch, book_bonus'],
    ['utm_content', 'The creative/placement', 'main_post, thread_1, pinned_comment, bio_link'],
  ];

  return (
    <div style={{ background: '#141414', border: '0.5px solid #2a2a2a', borderRadius: 12, padding: 20 }}>
      <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>How to Read This</h3>
      <p style={{ margin: '6px 0 14px', color: '#71717A', fontSize: 13, maxWidth: 760 }}>
        The goal is not just leads. The goal is to learn which places create profitable buyers. Use a unique link per channel/post, then compare opt-ins, buyer rate, and revenue per opt-in.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 10 }}>
        {rules.map(([tag, meaning, examples]) => (
          <div key={tag} style={{ background: '#0f0f0f', border: '0.5px solid #2a2a2a', borderRadius: 8, padding: 12 }}>
            <div style={{ color: 'var(--blue)', fontSize: 12, fontFamily: "'JetBrains Mono', monospace", fontWeight: 800 }}>{tag}</div>
            <div style={{ color: '#fff', fontSize: 13, fontWeight: 600, marginTop: 5 }}>{meaning}</div>
            <div style={{ color: '#71717A', fontSize: 12, marginTop: 5 }}>{examples}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Tracking() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      get(`/api/tracking/dashboard?days=${days}`),
      get(`/api/tracking/revenue-source-review?days=${days}&funnel_id=case-study`),
    ])
      .then(([dashboardData, reviewData]) => { setData(dashboardData); setReview(reviewData); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });
  }, [days]);

  const summary = data?.summary || {};
  const pages = data?.pages || [];
  const sources = data?.sources || [];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap', marginBottom: 24 }}>
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em', marginBottom: 2 }}>Tracking</h2>
          <div style={{ fontSize: 12, color: 'var(--muted)' }}>Opt-in pages, traffic sources, and revenue attribution.</div>
        </div>
        <select value={days} onChange={e => setDays(Number(e.target.value))} style={{ padding: '9px 12px', borderRadius: 8, border: '0.5px solid #2a2a2a', background: '#141414', color: '#fff', fontSize: 16 }}>
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
          <option value={365}>Last 365 days</option>
        </select>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--muted)' }}>Loading tracking data…</div>
      ) : error ? (
        <div style={{ background: 'rgba(239,68,68,0.12)', border: '0.5px solid rgba(239,68,68,0.35)', color: '#fca5a5', borderRadius: 12, padding: 18 }}>{error}</div>
      ) : (
        <div style={{ display: 'grid', gap: 18 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12 }}>
            <Card label="Page Views" value={fmtNum(summary.page_views)} />
            <Card label="Opt-ins" value={fmtNum(summary.optins)} tone="blue" sub={`${fmtPct(summary.optin_rate)} opt-in rate`} />
            <Card label="Buyers" value={fmtNum(summary.buyers)} tone="green" sub={`${fmtPct(summary.buyer_rate)} buyer rate`} />
            <Card label="Rev / Click" value={fmtMoney(summary.revenue_per_click)} tone="green" sub="Revenue divided by tracked CTA clicks" />
            <Card label="Revenue" value={fmtMoney(summary.revenue)} tone="green" sub={`${fmtMoney(summary.revenue_per_optin)} per opt-in`} />
          </div>

          <YoutubeVideoRevenue sources={sources} />

          <RevenueSourceReview review={review} />

          <TrackingLinkBuilder />

          <div style={{ background: '#141414', border: '0.5px solid #2a2a2a', borderRadius: 12, padding: 20, overflowX: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: 11, fontFamily: "'JetBrains Mono', monospace", fontWeight: 800, textTransform: 'uppercase', color: '#6B7280', letterSpacing: '0.08em' }}>Opt-in Pages</div>
                <div style={{ fontSize: 13, color: '#71717A', marginTop: 4 }}>Landing pages ranked by revenue, opt-ins, and views.</div>
              </div>
            </div>
            {pages.length === 0 ? (
              <div style={{ color: '#71717A', padding: 24, textAlign: 'center' }}>No tracked opt-in pages yet.</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '0.5px solid #2a2a2a' }}>
                    {['Page', 'Views', 'Clicks', 'Opt-ins', 'Opt-in %', 'Buyers', 'Revenue', 'Rev / Click', 'Rev / Opt-in', 'Top Source'].map(h => (
                      <th key={h} style={{ padding: '9px 10px', textAlign: h === 'Page' ? 'left' : 'right', color: '#6B7280', fontSize: 10, fontFamily: "'JetBrains Mono', monospace", textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pages.map(page => (
                    <tr key={`${page.funnel_id}-${page.page_url}`} style={{ borderBottom: '0.5px solid #2a2a2a' }}>
                      <td style={{ padding: '12px 10px', color: '#fff', fontWeight: 600, minWidth: 250 }}>
                        <div>{page.funnel_id}</div>
                        <div style={{ color: '#71717A', fontSize: 11, marginTop: 3, wordBreak: 'break-all' }}>{page.page_url}</div>
                      </td>
                      <td style={{ padding: '12px 10px', textAlign: 'right', color: '#d4d4d4', fontFamily: "'JetBrains Mono', monospace" }}>{fmtNum(page.page_views)}</td>
                      <td style={{ padding: '12px 10px', textAlign: 'right', color: '#d4d4d4', fontFamily: "'JetBrains Mono', monospace" }}>{fmtNum(page.cta_clicks)}</td>
                      <td style={{ padding: '12px 10px', textAlign: 'right', color: '#fff', fontFamily: "'JetBrains Mono', monospace" }}>{fmtNum(page.optins)}</td>
                      <td style={{ padding: '12px 10px', textAlign: 'right', color: 'var(--blue)', fontFamily: "'JetBrains Mono', monospace" }}>{fmtPct(page.optin_rate)}</td>
                      <td style={{ padding: '12px 10px', textAlign: 'right', color: '#fff', fontFamily: "'JetBrains Mono', monospace" }}>{fmtNum(page.buyers)}</td>
                      <td style={{ padding: '12px 10px', textAlign: 'right', color: 'var(--green)', fontFamily: "'JetBrains Mono', monospace" }}>{fmtMoney(page.revenue)}</td>
                      <td style={{ padding: '12px 10px', textAlign: 'right', color: '#d4d4d4', fontFamily: "'JetBrains Mono', monospace" }}>{fmtMoney(page.revenue_per_click)}</td>
                      <td style={{ padding: '12px 10px', textAlign: 'right', color: '#d4d4d4', fontFamily: "'JetBrains Mono', monospace" }}>{fmtMoney(page.revenue_per_optin)}</td>
                      <td style={{ padding: '12px 10px', textAlign: 'right', color: '#9CA3AF' }}>{page.top_source || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <SourceSummaryCards sources={sources} />

          <div style={{ background: '#141414', border: '0.5px solid #2a2a2a', borderRadius: 12, padding: 20, overflowX: 'auto' }}>
            <div style={{ fontSize: 11, fontFamily: "'JetBrains Mono', monospace", fontWeight: 800, textTransform: 'uppercase', color: '#6B7280', letterSpacing: '0.08em', marginBottom: 4 }}>Source Tracking</div>
            <div style={{ fontSize: 13, color: '#71717A', marginBottom: 14 }}>Lead/source tracking for opt-ins and existing dashboard attribution. Use this section when asking “how many leads came from direct or unknown?” Use the Buyer Revenue Review above when asking “how much revenue did those leads produce?”</div>
            {sources.length === 0 ? (
              <div style={{ color: '#71717A', padding: 24, textAlign: 'center' }}>No source-level data yet. Start sharing UTM links from the builder above.</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '0.5px solid #2a2a2a' }}>
                    {['Page', 'Source', 'Medium', 'Campaign', 'Content', 'Attribution', 'Views', 'Clicks', 'Opt-ins', 'Buyers', 'Revenue', 'Rev / Click', 'Rev / Opt-in'].map(h => (
                      <th key={h} style={{ padding: '9px 10px', textAlign: ['Source', 'Medium', 'Campaign', 'Content', 'Attribution', 'Page'].includes(h) ? 'left' : 'right', color: '#6B7280', fontSize: 10, fontFamily: "'JetBrains Mono', monospace", textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sources.map((source, idx) => (
                    <tr key={`${source.funnel_id}-${source.source}-${source.medium}-${source.campaign}-${source.content}-${idx}`} style={{ borderBottom: '0.5px solid #2a2a2a' }}>
                      <td style={{ padding: '12px 10px', color: '#9CA3AF' }}>{source.label || source.funnel_id}</td>
                      <td style={{ padding: '12px 10px', color: '#fff', fontWeight: 700 }}>{source.source}</td>
                      <td style={{ padding: '12px 10px', color: '#d4d4d4' }}>{source.medium}</td>
                      <td style={{ padding: '12px 10px', color: '#d4d4d4' }}>{source.campaign}</td>
                      <td style={{ padding: '12px 10px', color: '#9CA3AF' }} title={source.click_url || ''}>{source.content}</td>
                      <td style={{ padding: '12px 10px', color: source.attribution_type === 'sequence_click' ? 'var(--green)' : '#9CA3AF', whiteSpace: 'nowrap' }} title={source.click_url || ''}>
                        {source.sequence_slug || source.attribution_type || 'optin'}
                      </td>
                      <td style={{ padding: '12px 10px', textAlign: 'right', color: '#d4d4d4', fontFamily: "'JetBrains Mono', monospace" }}>{fmtNum(source.page_views)}</td>
                      <td style={{ padding: '12px 10px', textAlign: 'right', color: '#d4d4d4', fontFamily: "'JetBrains Mono', monospace" }}>{fmtNum(source.cta_clicks)}</td>
                      <td style={{ padding: '12px 10px', textAlign: 'right', color: '#fff', fontFamily: "'JetBrains Mono', monospace" }}>{fmtNum(source.optins)}</td>
                      <td style={{ padding: '12px 10px', textAlign: 'right', color: '#fff', fontFamily: "'JetBrains Mono', monospace" }}>{fmtNum(source.buyers)}</td>
                      <td style={{ padding: '12px 10px', textAlign: 'right', color: 'var(--green)', fontFamily: "'JetBrains Mono', monospace" }}>{fmtMoney(source.revenue)}</td>
                      <td style={{ padding: '12px 10px', textAlign: 'right', color: '#d4d4d4', fontFamily: "'JetBrains Mono', monospace" }}>{fmtMoney(source.revenue_per_click)}</td>
                      <td style={{ padding: '12px 10px', textAlign: 'right', color: '#d4d4d4', fontFamily: "'JetBrains Mono', monospace" }}>{fmtMoney(source.revenue_per_optin)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <TeachingPanel />
        </div>
      )}
    </div>
  );
}
